// docs/71 INV7 — trades between heroes in one World. The composer picks who to trade with (my
// other heroes hand over at once; another player's hero gets an offer to accept or decline),
// what this hero gives (items from its pack or worn, gold) and what it asks (items the other hero
// carries, gold), with a short note. The Trades tab lists offers waiting on me, offers I made, and
// what was settled lately. `TradeWatcher` toasts a new offer, and the answer to mine, wherever I am.
import { ItemIcon, itemRarity, itemTooltip } from './ItemCard';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, EmptyState, HeroSprite, Input, Modal, OryxIcon } from '../components';
import { useMyTrades, useNames, useUpgradesOf, useWorldHeroes, useWorldItems } from '../../core/subscriptions/hooks';
import { upgradedName } from '../../core/items/upgrades';
import { useSession } from '../../core/stores/session';
import { identityHex, tsToMs } from '../../core/net/connection';
import { rpc, useToasts } from '../../core/net/reducers';
import { CLASS_NAMES } from '../../core/sim/adapt';
import { SLOT } from '../../core/sim/gear';
import { playSfx } from '../../core/audio';
import { useOutfitting } from './outfitting';
import { tradeBlock } from '../screens/founding';
import { useInventory } from './store';

export const TRADE_OPEN = 0, TRADE_ACCEPTED = 1, TRADE_DECLINED = 2, TRADE_CANCELLED = 3, TRADE_LAPSED = 4;
const STATE_WORD = ['waiting', 'made', 'declined', 'withdrawn', 'lapsed'];
const NOTE_MAX = 140;
const ITEMS_MAX = 12;

type TradeRow = ReturnType<typeof useMyTrades>[number];
type ItemRow = ReturnType<typeof useWorldItems>[number];

/** Offers waiting on me in this World. */
export function useIncomingTrades(worldId: bigint | undefined): TradeRow[] {
  const me = useSession((s) => s.identity ?? '');
  const trades = useMyTrades();
  return useMemo(() => trades.filter((t) => t.worldId === worldId && t.state === TRADE_OPEN && identityHex(t.toOwner) === me && identityHex(t.fromOwner) !== me), [trades, worldId, me]);
}

/** Toasts for trades: a new offer to one of my heroes, and the answer to one of mine. */
export function TradeWatcher({ worldId }: { worldId: bigint | undefined }) {
  const me = useSession((s) => s.identity ?? '');
  const trades = useMyTrades();
  const heroes = useWorldHeroes(worldId);
  const push = useToasts((s) => s.push);
  const known = useRef<Map<string, number> | undefined>(undefined);
  const primed = useRef(false);
  useEffect(() => { const t = setTimeout(() => { primed.current = true; }, 2500); return () => clearTimeout(t); }, []);
  useEffect(() => {
    const seen = (known.current ??= new Map());
    const name = (id: bigint) => heroes.find((h) => h.id === id)?.name ?? 'a hero';
    for (const t of trades) {
      const k = t.id.toString();
      const was = seen.get(k);
      seen.set(k, t.state);
      if (!primed.current || was === t.state) continue;
      const mineFrom = identityHex(t.fromOwner) === me, mineTo = identityHex(t.toOwner) === me;
      if (mineFrom && mineTo) continue; // between my own heroes: the inventory already said so
      if (was === undefined && t.state === TRADE_OPEN && mineTo) { playSfx('ui.trade'); push(`${name(t.fromHero)} offers ${name(t.toHero)} a trade. Open ${name(t.toHero)}'s inventory → Trades.`, 'info'); }
      else if (mineFrom && t.state === TRADE_ACCEPTED) { playSfx('ui.coins'); push(`${name(t.toHero)} accepted ${name(t.fromHero)}'s trade.`, 'success'); }
      else if (mineFrom && t.state === TRADE_DECLINED) push(`${name(t.toHero)} declined ${name(t.fromHero)}'s trade.`, 'info');
      else if (t.state === TRADE_LAPSED) push(`A trade lapsed: ${t.reason || 'something in it changed hands'}.`, 'info');
    }
  }, [trades, heroes, me, push]);
  return null;
}

/* ---------- a trade drawn as two sides ---------- */

function Piece({ it, missing }: { it?: ItemRow; missing?: boolean }) {
  // docs/112: a traded piece carries its upgrades with it
  const upsOf = useUpgradesOf(useSession((s) => s.worldId));
  if (!it) return <li className="fk-trade__piece is-gone" title="No longer there">?</li>;
  const r = itemRarity(it.def, it.tier, it.relic);
  const ups = upsOf(it.id);
  return (
    <li className={`fk-trade__piece fk-inv__tile--${r} ${missing ? 'is-gone' : ''}`} title={itemTooltip(it.def, it.tier, it.name, it.relic, ups)} data-testid="trade-piece" data-item-name={it.name}>
      <ItemIcon def={it.def} tier={it.tier} name={it.name} scale={2} upgrades={ups} />
      <span className="fk-trade__piece-name">{upgradedName(it.name, ups)}</span>
    </li>
  );
}

function Side({ label, hero, items, gold, all }: { label: string; hero?: { id: bigint; species: number; name: string; oryxFrame: number; class: number }; items: readonly bigint[]; gold: number; all: readonly ItemRow[] }) {
  return (
    <div className="fk-trade__side">
      <div className="fk-trade__side-head">{hero && <HeroSprite heroId={hero.id} species={hero.species} frame={hero.oryxFrame} classId={CLASS_NAMES[hero.class] ?? 'fighter'} scale={2} />}<span><span className="fk-eyebrow">{label}</span><b>{hero?.name ?? 'a hero'}</b></span></div>
      <ul className="fk-trade__pieces">
        {items.map((id) => <Piece key={id.toString()} it={all.find((i) => i.id === id)} />)}
        {gold > 0 && <li className="fk-trade__piece fk-trade__gold" data-testid="trade-gold"><OryxIcon name="item_coin_bag" scale={2} /><span className="fk-trade__piece-name">{gold} gold</span></li>}
        {!items.length && !gold && label !== 'To' && <li className="fk-faint fk-trade__nothing">nothing</li>}
      </ul>
    </div>
  );
}

/** The Trades tab: waiting on me, waiting on them, lately settled. */
export function Trades({ worldId, heroId }: { worldId: bigint; heroId: bigint }) {
  const me = useSession((s) => s.identity ?? '');
  const trades = useMyTrades().filter((t) => t.worldId === worldId);
  const heroes = useWorldHeroes(worldId);
  const items = useWorldItems(worldId);
  const names = useNames(worldId);
  const [busy, setBusy] = useState<string>();
  const [composing, setComposing] = useState(false);
  const push = useToasts((s) => s.push);
  const hero = (id: bigint) => heroes.find((h) => h.id === id);
  const incoming = trades.filter((t) => t.state === TRADE_OPEN && identityHex(t.toOwner) === me && identityHex(t.fromOwner) !== me).sort((a, b) => (a.id < b.id ? 1 : -1));
  const outgoing = trades.filter((t) => t.state === TRADE_OPEN && identityHex(t.fromOwner) === me).sort((a, b) => (a.id < b.id ? 1 : -1));
  const recent = trades.filter((t) => t.state !== TRADE_OPEN).sort((a, b) => (a.id < b.id ? 1 : -1)).slice(0, 8);
  const act = async (key: string, fn: () => Promise<{ ok: boolean }>, ok: () => void) => { setBusy(key); const r = await fn(); setBusy(undefined); if (r.ok) ok(); };
  const card = (t: TradeRow, kind: 'in' | 'out' | 'past') => {
    const from = hero(t.fromHero), to = hero(t.toHero);
    const who = names[identityHex(kind === 'in' ? t.fromOwner : t.toOwner)] ?? 'another player';
    const when = tsToMs(t.settledAt ?? t.createdAt);
    return (
      <li key={t.id.toString()} className={`fk-trade fk-trade--${kind} fk-trade--s${t.state}`} data-testid="trade-card" data-trade-id={t.id.toString()} data-state={t.state} data-kind={kind}>
        <div className="fk-trade__meta">
          {kind === 'in' ? <><b>{who}</b> offers a trade</> : kind === 'out' ? <>Waiting on <b>{who}</b></> : <Badge tone={t.state === TRADE_ACCEPTED ? 'green' : t.state === TRADE_LAPSED ? 'amber' : 'grey'}>{STATE_WORD[t.state]}</Badge>}
          {when ? <span className="fk-faint">{new Date(when).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span> : null}
        </div>
        <div className="fk-trade__sides">
          <Side label="Gives" hero={from} items={t.giveItems} gold={t.giveGold} all={items} />
          <span className="fk-trade__arrows" aria-hidden="true">⇄</span>
          <Side label={t.askItems.length || t.askGold ? 'Asks of' : 'To'} hero={to} items={t.askItems} gold={t.askGold} all={items} />
        </div>
        {t.note && <p className="fk-trade__note">“{t.note}”</p>}
        {t.state === TRADE_LAPSED && t.reason && <p className="fk-faint">{t.reason}</p>}
        {kind === 'in' && (
          <div className="fk-trade__actions">
            <Button small variant="primary" loading={busy === `a${t.id}`} disabled={!!busy} onClick={() => void act(`a${t.id}`, () => rpc.acceptTrade(t.id), () => { playSfx('ui.trade'); push('The trade is made.', 'success'); })} data-testid="trade-accept">Accept</Button>
            <Button small variant="ghost" loading={busy === `d${t.id}`} disabled={!!busy} onClick={() => void act(`d${t.id}`, () => rpc.declineTrade(t.id), () => push('Declined.', 'info'))} data-testid="trade-decline">Decline</Button>
          </div>
        )}
        {kind === 'out' && (
          <div className="fk-trade__actions">
            <Button small variant="ghost" loading={busy === `c${t.id}`} disabled={!!busy} onClick={() => void act(`c${t.id}`, () => rpc.cancelTrade(t.id), () => push('Offer withdrawn.', 'info'))} data-testid="trade-cancel">Withdraw</Button>
          </div>
        )}
      </li>
    );
  };
  return (
    <div className="fk-trades" data-testid="inv-trades">
      <div className="fk-trades__bar">
        <p className="fk-faint">Trade with any hero in this World: your own hand over at once, other players' heroes answer your offer.</p>
        <Button variant="primary" onClick={() => setComposing(true)} data-testid="trade-new" icon={<span aria-hidden="true">⇄</span>}>New trade</Button>
      </div>
      <div className="fk-trades__cols">
        <section aria-label="Offers to you"><h3 className="fk-eyebrow">Offers to you {incoming.length > 0 && <Badge tone="amber" filled>{incoming.length}</Badge>}</h3>
          {incoming.length ? <ul className="fk-trades__list">{incoming.map((t) => card(t, 'in'))}</ul> : <EmptyState glyph="⇄" title="Nothing waiting">Offers from other players land here.</EmptyState>}
        </section>
        <section aria-label="Your offers"><h3 className="fk-eyebrow">Your offers</h3>
          {outgoing.length ? <ul className="fk-trades__list">{outgoing.map((t) => card(t, 'out'))}</ul> : <EmptyState glyph="✉" title="None out">Offers you make wait here for an answer.</EmptyState>}
        </section>
        <section aria-label="Lately"><h3 className="fk-eyebrow">Lately</h3>
          {recent.length ? <ul className="fk-trades__list">{recent.map((t) => card(t, 'past'))}</ul> : <EmptyState glyph="☍" title="No trades yet">Settled trades are kept here.</EmptyState>}
        </section>
      </div>
      {composing && <TradeComposer worldId={worldId} fromHero={heroId} initialGive={[]} onClose={() => setComposing(false)} />}
    </div>
  );
}

/* ---------- the composer ---------- */

function PickList({ items, picked, onToggle, testId, disabledWhy }: { items: readonly ItemRow[]; picked: Set<string>; onToggle(id: bigint): void; testId: string; disabledWhy(i: ItemRow): string | undefined }) {
  const upsOf = useUpgradesOf(useSession((s) => s.worldId));
  if (!items.length) return <p className="fk-faint fk-trade__empty">Carries nothing to trade.</p>;
  return (
    <ul className="fk-trade__pick" data-testid={testId}>
      {items.map((i) => {
        const why = disabledWhy(i);
        const on = picked.has(i.id.toString());
        const worn = i.equippedSlot !== undefined && i.equippedSlot !== null;
        return (
          <li key={i.id.toString()}>
            <button type="button" className={`fk-trade__pick-item fk-inv__tile--${itemRarity(i.def, i.tier, i.relic)} ${on ? 'is-on' : ''}`} aria-pressed={on} disabled={!!why} title={why ?? `${itemTooltip(i.def, i.tier, i.name, i.relic, upsOf(i.id))}${worn ? '\n(worn)' : ''}`} onClick={() => onToggle(i.id)} data-testid="trade-pick" data-item-name={i.name}>
              <ItemIcon def={i.def} tier={i.tier} name={i.name} scale={2} upgrades={upsOf(i.id)} />
              <span className="fk-trade__pick-name">{upgradedName(i.name, upsOf(i.id))}{worn && <small> worn</small>}</span>
              <span className="fk-trade__pick-check" aria-hidden="true">{on ? '✓' : ''}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function TradeComposer({ worldId, fromHero, initialGive, onClose }: { worldId: bigint; fromHero: bigint; initialGive: bigint[]; onClose(): void }) {
  const me = useSession((s) => s.identity ?? '');
  const heroes = useWorldHeroes(worldId);
  const items = useWorldItems(worldId);
  const names = useNames(worldId);
  const push = useToasts((s) => s.push);
  const openInv = useInventory((s) => s.open);
  const from = heroes.find((h) => h.id === fromHero);
  const [to, setTo] = useState<bigint>();
  const [give, setGive] = useState<Set<string>>(() => new Set(initialGive.map(String)));
  const [ask, setAsk] = useState<Set<string>>(new Set());
  const [giveGold, setGiveGold] = useState(0);
  const [askGold, setAskGold] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const target = heroes.find((h) => h.id === to);
  const mine = target ? identityHex(target.owner) === me : false;
  const others = heroes.filter((h) => h.id !== fromHero && h.status !== 2);
  const myOthers = others.filter((h) => identityHex(h.owner) === me);
  const theirs = others.filter((h) => identityHex(h.owner) !== me);
  // docs/146 OG3: nothing changes hands between players while either is still outfitting
  const outfit = useOutfitting(worldId);
  const blockOf = (h: (typeof heroes)[number]) => tradeBlock({ sameOwner: identityHex(h.owner) === me, gathering: outfit.gathering, fromName: from?.name ?? 'Your hero', toName: h.name, fromOutfitting: outfit.mine, toOutfitting: outfit.of(identityHex(h.owner)) });
  const theirsClosed = theirs.length > 0 && (outfit.gathering || outfit.mine) ? tradeBlock({ sameOwner: false, gathering: outfit.gathering, fromName: from?.name ?? 'Your hero', toName: '', fromOutfitting: outfit.mine, toOutfitting: false }) : undefined;
  const sort = (a: ItemRow, b: ItemRow) => (a.equippedSlot ?? 99) - (b.equippedSlot ?? 99) || (a.id < b.id ? -1 : 1);
  const myItems = items.filter((i) => i.heroId === fromHero).sort(sort);
  const theirItems = items.filter((i) => i.heroId === to).sort(sort);
  const toggle = (set: Set<string>, put: (s: Set<string>) => void) => (id: bigint) => { const s = new Set(set); const k = id.toString(); if (s.has(k)) s.delete(k); else if (s.size < ITEMS_MAX) s.add(k); put(s); };   // the pick's button clicks by itself (ui/sfx.ts)
  const torsoWhy = (i: ItemRow) => (i.equippedSlot === SLOT.Torso ? 'The armour being worn: swap it first' : outfit.kit.has(i.id) ? `Starting kit stays with its hero ${outfit.gathering ? 'until the World begins' : 'until its company sets out'}` : undefined);
  const empty = !give.size && !ask.size && !giveGold && !askGold;
  const goldOk = !!from && giveGold <= from.gold && (!target || askGold <= target.gold);
  const send = async () => {
    if (!from || !target) return;
    setBusy(true);
    const r = await rpc.offerTrade(from.id, target.id, [...give].map(BigInt), giveGold, [...ask].map(BigInt), askGold, note);
    setBusy(false);
    if (!r.ok) return;
    playSfx('ui.trade');
    push(mine ? `${from.name} and ${target.name} trade.` : `Offer sent to ${names[identityHex(target.owner)] ?? 'the other player'}.`, 'success');
    onClose();
    if (!mine) openInv(from.id, { tab: 'trades' });
  };
  const heroButton = (h: (typeof heroes)[number], why = blockOf(h)) => (
    <li key={h.id.toString()}>
      <button type="button" className={`fk-trade__who ${to === h.id ? 'is-on' : ''}`} aria-pressed={to === h.id} disabled={!!why} title={why} onClick={() => { setTo(h.id); setAsk(new Set()); setAskGold(0); }} data-testid="trade-to" data-hero-name={h.name}>
        <HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={CLASS_NAMES[h.class] ?? 'fighter'} scale={2} />
        <span><b>{h.name}</b><small>L{h.level} {CLASS_NAMES[h.class]}{identityHex(h.owner) === me ? '' : ` · ${names[identityHex(h.owner)] ?? 'another player'}`}{h.status === 1 ? ' · in the field' : ''}</small></span>
      </button>
    </li>
  );
  return (
    <Modal open onClose={onClose} eyebrow={from ? `${from.name} trades` : 'Trade'} title={target ? `With ${target.name}` : 'Who with?'} size="wide" testId="trade-composer" className="fk-trade-composer"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!target || empty || !goldOk || busy} loading={busy} onClick={() => void send()} data-testid="trade-send">{mine ? 'Hand over' : 'Send offer'}</Button>
      </>}>
      <div className="fk-trade__compose">
        <section className="fk-trade__whos" aria-label="Trade with">
          {myOthers.length > 0 && <><h3 className="fk-eyebrow">Your heroes</h3><ul>{myOthers.map((h) => heroButton(h))}</ul></>}
          {theirs.length > 0 && <><h3 className="fk-eyebrow">Other players' heroes</h3>{theirsClosed
            ? <p className="fk-faint fk-trade__closed" data-testid="trade-players-closed">{theirsClosed} Until then your own heroes may hand each other anything.</p>
            : <ul>{theirs.map((h) => heroButton(h))}</ul>}</>}
          {!others.length && <EmptyState glyph="☍" title="Nobody else here">{outfit.mine ? 'Recruit another hero to hand gear between them; players trade once the World is under way.' : 'Other heroes in this World show here.'}</EmptyState>}
        </section>
        <section className="fk-trade__give" aria-label="You give">
          <h3 className="fk-eyebrow">{from?.name ?? 'You'} gives</h3>
          <PickList items={myItems} picked={give} onToggle={toggle(give, setGive)} testId="trade-give" disabledWhy={torsoWhy} />
          <label className="fk-trade__goldline"><OryxIcon name="item_coin_bag" scale={1} /> Gold <Input small type="number" min={0} max={from?.gold ?? 0} value={giveGold} onChange={(e) => setGiveGold(Math.max(0, Math.min(from?.gold ?? 0, Math.floor(Number(e.target.value) || 0))))} data-testid="trade-give-gold" /> <span className="fk-faint">of {from?.gold ?? 0}</span></label>
        </section>
        <section className="fk-trade__ask" aria-label="You ask">
          <h3 className="fk-eyebrow">{target ? `${target.name} gives` : 'They give'}</h3>
          {!target ? <p className="fk-faint">Pick a hero to see what they carry.</p> : (
            <>
              <PickList items={theirItems} picked={ask} onToggle={toggle(ask, setAsk)} testId="trade-ask" disabledWhy={torsoWhy} />
              <label className="fk-trade__goldline"><OryxIcon name="item_coin_bag" scale={1} /> Gold <Input small type="number" min={0} max={target.gold} value={askGold} onChange={(e) => setAskGold(Math.max(0, Math.min(target.gold, Math.floor(Number(e.target.value) || 0))))} data-testid="trade-ask-gold" /> <span className="fk-faint">of {target.gold}</span></label>
            </>
          )}
          {target && !mine && <label className="fk-trade__noteline">Note <Input small maxLength={NOTE_MAX} value={note} placeholder="A word with the offer (optional)" onChange={(e) => setNote(e.target.value)} data-testid="trade-note" /></label>}
          {target && mine && <p className="fk-faint">Both heroes are yours: it happens at once.</p>}
        </section>
      </div>
    </Modal>
  );
}
